import React, { useState, useEffect } from 'react';
import {
  UserProfile,
  MonteCarloResponse,
  OAS_DELAY_STRATEGIES,
  CPP_DELAY_STRATEGIES,
  WORKING_PROFILES,
  INCOME_PROFILES,
  OtherIncomeSource
} from '../types/advanced-retirement';
import { BaseScenarioData } from '../types/shared';
import { AdvancedRetirementAPI } from '../services/advanced-api';
import { calculateRRIFMinimumWithdrawal } from '../utils/rrif-schedule';
import { takeHandoff } from '../utils/forecastToRetirement';
import OASService from '../services/oas-service';
import DisclaimerModal from './DisclaimerModal';
import GuidanceModal from './GuidanceModal';
import GoogleAdInterstitial from './GoogleAdInterstitial';
import FormattedInput from './FormattedInput';
import {
  GeneralSettingsSection,
  MyInformationSection,
  SpouseInformationSection,
  FinancialBalancesSection,
  InvestmentSettingsSection,
  IncomeEmploymentSection,
  GovernmentBenefitsSection,
  PensionSection,
  OtherIncomeSection,
  SpendingLifestyleSection,
  CapitalGainsSection,
  AdvancedSettingsSection
} from './AdvancedRetirementPlannerSections';
import ScenariosTab from './ScenariosTab';
import RetirementResults from './RetirementResults';
import RetirementResultsSummary from './RetirementResultsSummary';
import RetirementResultsCharts from './RetirementResultsCharts';
import RetirementResultsTable from './RetirementResultsTable';
import MonthlyBreakdown from './MonthlyBreakdown';
import TaxBracketAlerts from './TaxBracketAlerts';
import AccountPreservationScore from './AccountPreservationScore';
import WithdrawalStrategyExplainer from './WithdrawalStrategyExplainer';
import { TylerState, useTylerStateUpdate, updateReact } from '../utils/TylerState';
import RetirementPDFService from '../services/retirement-pdf-service';
import retirementScenariosService from '../services/retirement-scenarios';
import { SavedRetirementScenario } from '../types/retirementComparison';

interface SavedRRSPScenario {
  id: string;
  name: string;
  data: any;
  createdAt: string;
}

interface AdvancedRetirementPlannerProps {
  initialData?: any;
  onSave?: (scenarioData: any) => void;
  isGuestMode?: boolean;
  results?: MonteCarloResponse | null;
  loading?: boolean;
  saveName?: string;
  triggerAnalysis?: boolean;
  onAnalysisTriggered?: () => void;
  savedScenarios?: SavedRRSPScenario[];
  onLoadScenario?: (id: string) => void;
  onDeleteScenario?: (id: string) => void;
}

/**
 * The year-by-year balances the engine actually produced, shaped for the account chart.
 *
 * The chart used to re-project from scratch instead - starting balances plus a hardcoded $40,000
 * withdrawal, a hardcoded $18,000 contribution and a flat 30% tax at death - and render that beside
 * the real optimiser output under the same heading. It waited for the calculation to finish, then
 * ignored every number in it.
 */
export const toChartSeries = (projection?: any[]) =>
  (projection || []).map((row: any) => ({
    year: row.year,
    age: row.age,
    rrsp: row.rrsp_balance || 0,
    tfsa: row.tfsa_balance || 0,
    other: row.non_registered_balance || 0,
    total: row.total_balance || 0,
  }));

const AdvancedRetirementPlanner: React.FC<AdvancedRetirementPlannerProps> = ({ saveName,
  initialData,
  onSave,
  isGuestMode = false,
  results: externalResults,
  loading: externalLoading,
  triggerAnalysis,
  onAnalysisTriggered,
  savedScenarios: rrspScenarios = [],
  onLoadScenario,
  onDeleteScenario
}) => {
  // Subscribe to TylerState updates
  useTylerStateUpdate();

  const [userProfile, setUserProfile] = useState<UserProfile>(
    initialData ? AdvancedRetirementAPI.createUserProfileFromData(initialData) : AdvancedRetirementAPI.createDefaultUserProfile()
  );
  const [internalResults, setInternalResults] = useState<MonteCarloResponse | null>(null);
  const [taxOptimizationResults, setTaxOptimizationResults] = useState<any | null>(null);
  const [comparisonResults, setComparisonResults] = useState<any | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [isInputSectionVisible, setIsInputSectionVisible] = useState(true);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showAdModal, setShowAdModal] = useState(false);
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [guidanceTitle, setGuidanceTitle] = useState('');
  const [guidanceContent, setGuidanceContent] = useState('');
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<string>('ALL');
  const [showScenarioDistribution, setShowScenarioDistribution] = useState(false);
  const [showAccountContinuity, setShowAccountContinuity] = useState(false);
  const [selectedContinuityAccount, setSelectedContinuityAccount] = useState<string>('TFSA');
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);
  const [showTaxBracketAlerts, setShowTaxBracketAlerts] = useState(false);
  const [showStrategyExplainer, setShowStrategyExplainer] = useState(false);
  const [inflationView, setInflationView] = useState<'real' | 'nominal'>('real');
  const [internalLoading, setInternalLoading] = useState(false);
  const [errors, setErrors] = useState<string[] | null>(null);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Saved scenarios management (following car calculator pattern)
  const [savedScenarios, setSavedScenarios] = useState<SavedRetirementScenario[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveDescription, setSaveDescription] = useState('');

  // Comment out or remove unused investment income variables
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedInvestmentIncomeVariables = {
      rrspInvestmentIncome: 0,
      tfsaInvestmentIncome: 0,
      corporateInvestmentIncome: 0
  };

  // Use external results/loading if provided, otherwise use internal state
  const results = externalResults !== undefined && externalResults !== null ? externalResults : internalResults;
  
  // Debug: Log the results state
  console.log('Results state:', { externalResults, internalResults, finalResults: results });

  // Watch for external trigger to show disclaimer
  useEffect(() => {
    if (triggerAnalysis) {
      setShowDisclaimerModal(true);
      onAnalysisTriggered?.();
    }
  }, [triggerAnalysis, onAnalysisTriggered]);

  // Load saved scenarios on mount
  useEffect(() => {
    loadSavedScenarios();
  }, []);

  // Helper: Get scenario save limit based on tier
  const getScenarioCap = (tier: string): number => {
    return tier === 'supporter' ? Infinity : 1;
  };

  // Load saved scenarios from backend
  const loadSavedScenarios = async () => {
    const currentUser = TylerState.user;
    if (!currentUser?.email) {
      setSavedScenarios([]);
      return;
    }
    try {
      const scenariosArray = await retirementScenariosService.getRetirementScenariosArray();
      console.log('Loading retirement scenarios from backend:', scenariosArray);
      setSavedScenarios(scenariosArray);
    } catch (error) {
      console.error('Error loading retirement scenarios:', error);
      setSavedScenarios([]);
    }
  };

  // Handle section navigation with toggle functionality
  const handleSectionClick = (sectionId: string) => {
    if (activeSection === sectionId) {
      // If clicking the same section, toggle visibility
      setIsInputSectionVisible(!isInputSectionVisible);
    } else {
      // If clicking a different section, switch to it and show
      setActiveSection(sectionId);
      setIsInputSectionVisible(true);
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    setShowDisclaimerModal(false);

    // AUTO-SAVE: If logged in, save scenario on analyze (like car calculator does)
    const currentUser = TylerState.user;
    if (currentUser) {
      const cap = getScenarioCap(currentUser.tier || 'free');
      const scenarioName = (saveName || '').trim();

      if (scenarioName) {
        const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === scenarioName.toLowerCase());

        // Only save if: 1) updating existing OR 2) under cap for new scenario
        if (existingScenario || savedScenarios.length < cap) {
          try {
            if (existingScenario) {
              await retirementScenariosService.updateRetirementScenario(existingScenario.id, {
                name: scenarioName,
                description: saveDescription.trim() || undefined,
                data: userProfile
              });
              console.log('Auto-saved retirement scenario (updated):', scenarioName);
            } else {
              await retirementScenariosService.saveRetirementScenario({
                name: scenarioName,
                description: saveDescription.trim(),
                data: userProfile
              });
              console.log('Auto-saved retirement scenario (new):', scenarioName);
            }
            await loadSavedScenarios();
          } catch (error) {
            console.error('Auto-save failed:', error);
          }
        }
      }
    }

    // Always run the unified analysis after disclaimer acceptance
    handleRunRetirementAnalysis();
  };

  const handleRunSimulation = async () => {
    setInternalLoading(true);
    setErrors([]);

    try {
      const analysisResults = await AdvancedRetirementAPI.runAnalysis(userProfile);
      setInternalResults(analysisResults);
    } catch (error) {
      console.error('Simulation error:', error);
      setErrors(['Failed to run analysis. Please try again.']);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleRunRetirementAnalysis = async () => {
    setIsOptimizing(true);
    setErrors([]);

    try {
      // Check if user wants to lock OAS/CPP ages or optimize them
      const oasLocked = userProfile.oasDelayStrategy !== 'Calculate Suggested Strategy';
      const cppLocked = userProfile.cppDelayStrategy !== 'Calculate Suggested Strategy';

      // Extract age from strategy string (e.g., "Start early at 60" -> 60)
      const extractAge = (strategy: string): number | null => {
        const match = strategy.match(/\d+/);
        return match ? parseInt(match[0]) : null;
      };

      const lockedOasAge = oasLocked ? extractAge(userProfile.oasDelayStrategy) : null;
      const lockedCppAge = cppLocked ? extractAge(userProfile.cppDelayStrategy) : null;

      console.log('[RETIREMENT ANALYSIS] Starting analysis...');
      console.log(`[RETIREMENT ANALYSIS] OAS: ${oasLocked ? `Locked at ${lockedOasAge}` : 'Optimize'}`);
      console.log(`[RETIREMENT ANALYSIS] CPP: ${cppLocked ? `Locked at ${lockedCppAge}` : 'Optimize'}`);

      const optimizationResults = await AdvancedRetirementAPI.optimizeTaxStrategy(
        userProfile,
        10, // 10 Monte Carlo runs for optimization
        lockedOasAge,
        lockedCppAge
      );

      console.log('[RETIREMENT ANALYSIS] Results:', optimizationResults);
      setTaxOptimizationResults(optimizationResults);

      // Also run comparison (base vs optimized) for user messaging
      console.log('[RETIREMENT ANALYSIS] Running comparison analysis...');
      try {
        const comparisonData = await AdvancedRetirementAPI.compareRetirementStrategies(userProfile, 100);
        console.log('[RETIREMENT ANALYSIS] Comparison results:', comparisonData);
        setComparisonResults(comparisonData);
      } catch (compError) {
        console.error('[RETIREMENT ANALYSIS] Comparison failed (non-critical):', compError);
        // Don't fail the whole analysis if comparison fails
        setComparisonResults(null);
      }

      // Clear old results
      setInternalResults(null);

      // Navigate to first results section
      setActiveSection('results-summary');
      setIsInputSectionVisible(false);
    } catch (error: any) {
      console.error('Retirement analysis error:', error);
      setErrors([error.message || 'Failed to run analysis. Please try again.']);
    } finally {
      setIsOptimizing(false);
    }
  };

  // Update age when birth year/month changes
  useEffect(() => {
    if (userProfile.age && userProfile.currentYear) {
      const newProfile = AdvancedRetirementAPI.updateAgeFromBirth(userProfile);
      if (newProfile.age !== userProfile.age) {
        setUserProfile(newProfile);
      }
    }
  }, [userProfile]);

  // Arriving from the Personal Forecast: apply the balances it carried over, and keep what it had
  // to work out so the user sees it rather than discovering it in the results.
  const [handoffNotes, setHandoffNotes] = useState<string[]>([]);
  useEffect(() => {
    const h = takeHandoff();
    if (!h) return;
    setUserProfile(prev => ({
      ...prev,
      ...(h.age ? { age: h.age } : {}),
      currentYear: h.currentYear || prev.currentYear,
      retirementAge: h.retirementAge || prev.retirementAge,
      rrspBalance: h.rrspBalance,
      tfsaBalance: h.tfsaBalance,
      nonRegisteredBalance: h.nonRegisteredBalance,
      rrspMonthlyContribution: h.rrspMonthlyContribution,
      rrspMonthlyContributionEnabled: h.rrspMonthlyContribution > 0,
      tfsaMonthlyContribution: h.tfsaMonthlyContribution,
      tfsaMonthlyContributionEnabled: h.tfsaMonthlyContribution > 0,
      nonRegisteredMonthlyContribution: h.nonRegisteredMonthlyContribution,
      nonRegisteredMonthlyContributionEnabled: h.nonRegisteredMonthlyContribution > 0,
    }));
    setHandoffNotes([
      `Brought over from your Personal Forecast: RRSP $${h.rrspBalance.toLocaleString()}, `
      + `TFSA $${h.tfsaBalance.toLocaleString()}, non-registered $${h.nonRegisteredBalance.toLocaleString()}, `
      + `retiring at ${h.retirementAge}, spending $${h.annualSpending.toLocaleString()} a year.`,
      ...h.assumptions,
    ]);
  }, []);

  // "Load Scenario" (General Settings) hands the saved data down as a new initialData after mount; apply it
  useEffect(() => {
    if (initialData) {
      setUserProfile(AdvancedRetirementAPI.createUserProfileFromData(initialData));
    }
  }, [initialData]);

  // Update spouse age when spouse birth year/month changes
  useEffect(() => {
    if (userProfile.spouseAge && userProfile.currentYear) {
      const newProfile = AdvancedRetirementAPI.updateSpouseAgeFromBirth(userProfile);
      if (newProfile.spouseAge !== userProfile.spouseAge) {
        setUserProfile(newProfile);
      }
    }
  }, [userProfile]);

  const handleInputChange = (field: keyof UserProfile, value: any) => {
    setUserProfile(prev => {
      const updated = { ...prev, [field]: value };

      // Every spouse input is gated on spouseAge, so give it a value the moment the user says they are married
      if (field === 'maritalStatus' && value === 'Married/Common-law') {
        if (!prev.spouseAge) updated.spouseAge = prev.age;
        // The engine only models the spouse's life expectancy when a gender is posted; match what the selects show
        if (!prev.spouseGender) updated.spouseGender = 'Female';
        if (!prev.spouseLifeExpectancyScenario) updated.spouseLifeExpectancyScenario = 'General';
      }

      // Clear spouse fields if changing from married to single
      if (field === 'maritalStatus' && value === 'Single') {
        // Clear all spouse-related fields
        const spouseFields = [
          'spouseAge', 'spouseGender', 'spouseLifeExpectancyScenario',
          'spouseCurrentIncome', 'spouseExpectedRetirementIncome',
          'spouseRrspBalance', 'spouseRrifBalance', 'spouseTfsaBalance',
          'spouseTfsaContributionRoom', 'spouseTfsaAnnualIncrease',
          'spouseNonRegisteredBalance', 'spouseCorporateBalance',
          'spouseRrspReturnRate', 'spouseRrspRiskLevel', 'spouseRrifReturnRate',
          'spouseRrifRiskLevel', 'spouseTfsaReturnRate', 'spouseTfsaRiskLevel',
          'spouseNonRegisteredReturnRate', 'spouseNonRegisteredRiskLevel',
          'spouseCorporateReturnRate', 'spouseCorporateRiskLevel',
          'spouseWorkingProfile', 'spouseWorkingAge', 'spouseRetirementAge',
          'spouseCppBenefit', 'spouseOasBenefit', 'spouseOasDelayStrategy',
          'spouseCppDelayStrategy', 'spousePensionAmount', 'spousePensionStartAge',
          'spousePensionInflationRate', 'spousePensionSurvivorBenefitEnabled',
          'spouseOtherIncomeAmount', 'spouseOtherIncomeStartAge', 'spouseOtherIncomeProfile'
        ] as const;
        
        spouseFields.forEach(spouseField => {
          (updated as any)[spouseField] = undefined;
        });
      }
      
      return updated;
    });
    setErrors([]);
  };

  // Helper function to apply inflation adjustments
  const applyInflation = (value: number, year: number): number => {
    if (inflationView === 'real') {
      return value; // Already in real dollars
    }

    // Inflate to nominal dollars
    const currentYear = userProfile.currentYear || new Date().getFullYear();
    const inflationRate = userProfile.inflationRate || 0.02;
    const yearsInFuture = year - currentYear;

    return value * Math.pow(1 + inflationRate, yearsInFuture);
  };



  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatCurrencyWithColor = (value: number) => {
    const formatted = formatCurrency(value);
    if (value < 0) {
      return <span style={{ color: '#dc2626' }}>{formatted}</span>;
    }
    return formatted;
  };

  // Tax calculation function - now uses proper tax service
  const calculateTaxes = (age: number, taxableIncome: number) => {
    // Use the proper tax service that includes both federal and provincial taxes
    // This will give us the correct ~40% rates instead of the simplified ~7% rates
    
    // For now, implement proper Canadian tax calculation inline
    // until we can integrate with the backend tax service
    
    const province = userProfile.province || "Ontario";
    const currentAge = userProfile.age;
    const inflationYear = age - currentAge;
    
    // 2024 Canadian Federal Tax Brackets (NO inflation adjustment - brackets are fixed)
    const federalBrackets = [
      { min: 0, max: 55867, rate: 0.15 },
      { min: 55867, max: 111733, rate: 0.205 },
      { min: 111733, max: 173205, rate: 0.26 },
      { min: 173205, max: 246752, rate: 0.29 },
      { min: 246752, max: Infinity, rate: 0.33 }
    ];
    
    // 2024 Provincial Tax Brackets for major provinces (NO inflation adjustment)
    const getProvincialBrackets = (province: string) => {
      switch (province) {
        case "Ontario":
          return [
            { min: 0, max: 51446, rate: 0.0505 },
            { min: 51446, max: 102894, rate: 0.0915 },
            { min: 102894, max: 150000, rate: 0.1116 },
            { min: 150000, max: 220000, rate: 0.1216 },
            { min: 220000, max: Infinity, rate: 0.1316 }
          ];
        case "Quebec":
          return [
            { min: 0, max: 51446, rate: 0.14 },
            { min: 51446, max: 102894, rate: 0.19 },
            { min: 102894, max: 150000, rate: 0.24 },
            { min: 150000, max: Infinity, rate: 0.2575 }
          ];
        case "British Columbia":
          return [
            { min: 0, max: 45654, rate: 0.0506 },
            { min: 45654, max: 91310, rate: 0.077 },
            { min: 91310, max: 104835, rate: 0.105 },
            { min: 104835, max: 127299, rate: 0.1229 },
            { min: 127299, max: 172602, rate: 0.147 },
            { min: 172602, max: 240716, rate: 0.168 },
            { min: 240716, max: Infinity, rate: 0.205 }
          ];
        case "Alberta":
          return [
            { min: 0, max: 148269, rate: 0.10 },
            { min: 148269, max: 177922, rate: 0.12 },
            { min: 177922, max: 237230, rate: 0.13 },
            { min: 237230, max: 355845, rate: 0.14 },
            { min: 355845, max: Infinity, rate: 0.15 }
          ];
        case "Manitoba":
          return [
            { min: 0, max: 36842, rate: 0.108 },
            { min: 36842, max: 79625, rate: 0.1275 },
            { min: 79625, max: Infinity, rate: 0.174 }
          ];
        case "Saskatchewan":
          return [
            { min: 0, max: 49720, rate: 0.105 },
            { min: 49720, max: 142058, rate: 0.125 },
            { min: 142058, max: 162244, rate: 0.145 },
            { min: 162244, max: Infinity, rate: 0.15 }
          ];
        case "Nova Scotia":
          return [
            { min: 0, max: 29590, rate: 0.0879 },
            { min: 29590, max: 59180, rate: 0.1495 },
            { min: 59180, max: 93000, rate: 0.1667 },
            { min: 93000, max: 150000, rate: 0.175 },
            { min: 150000, max: Infinity, rate: 0.21 }
          ];
        case "New Brunswick":
          return [
            { min: 0, max: 47715, rate: 0.0968 },
            { min: 47715, max: 95431, rate: 0.1482 },
            { min: 95431, max: 176756, rate: 0.1652 },
            { min: 176756, max: Infinity, rate: 0.1995 }
          ];
        case "Newfoundland and Labrador":
          return [
            { min: 0, max: 41447, rate: 0.087 },
            { min: 41447, max: 82894, rate: 0.145 },
            { min: 82894, max: 148027, rate: 0.158 },
            { min: 148027, max: 207239, rate: 0.173 },
            { min: 207239, max: 264750, rate: 0.183 },
            { min: 264750, max: 529500, rate: 0.208 },
            { min: 529500, max: 1059000, rate: 0.213 },
            { min: 1059000, max: Infinity, rate: 0.218 }
          ];
        case "Prince Edward Island":
          return [
            { min: 0, max: 31984, rate: 0.098 },
            { min: 31984, max: 63969, rate: 0.138 },
            { min: 63969, max: Infinity, rate: 0.167 }
          ];
        case "Northwest Territories":
          return [
            { min: 0, max: 48326, rate: 0.059 },
            { min: 48326, max: 96655, rate: 0.086 },
            { min: 96655, max: 157139, rate: 0.122 },
            { min: 157139, max: Infinity, rate: 0.1405 }
          ];
        case "Yukon":
          return [
            { min: 0, max: 55867, rate: 0.064 },
            { min: 55867, max: 111733, rate: 0.09 },
            { min: 111733, max: 173205, rate: 0.109 },
            { min: 173205, max: 500000, rate: 0.128 },
            { min: 500000, max: Infinity, rate: 0.15 }
          ];
        case "Nunavut":
          return [
            { min: 0, max: 50877, rate: 0.04 },
            { min: 50877, max: 101754, rate: 0.07 },
            { min: 101754, max: 165429, rate: 0.09 },
            { min: 165429, max: Infinity, rate: 0.115 }
          ];
        default:
          // Default to Ontario if province not supported
          return [
            { min: 0, max: 51446, rate: 0.0505 },
            { min: 51446, max: 102894, rate: 0.0915 },
            { min: 102894, max: 150000, rate: 0.1116 },
            { min: 150000, max: 220000, rate: 0.1216 },
            { min: 220000, max: Infinity, rate: 0.1316 }
          ];
      }
    };
    
    const provincialBrackets = getProvincialBrackets(province);
    
    // IMPORTANT: Tax brackets are NOT adjusted for inflation
    // Only the income should be inflation-adjusted, not the brackets
    // This is the key fix - tax brackets remain constant in real terms
    
    // Calculate federal tax
    let federalTax = 0;
    let remainingIncome = taxableIncome;
    
    for (const bracket of federalBrackets) {
      if (remainingIncome > bracket.min) {
        const taxableInBracket = Math.min(remainingIncome - bracket.min, bracket.max - bracket.min);
        federalTax += taxableInBracket * bracket.rate;
        remainingIncome -= taxableInBracket;
      }
      
      if (remainingIncome <= 0) break;
    }
    
    // Calculate provincial tax (use fresh remainingIncome variable)
    let provincialTax = 0;
    let remainingIncomeProvincial = taxableIncome;
    
    for (const bracket of provincialBrackets) {
      if (remainingIncomeProvincial > bracket.min) {
        const taxableInBracket = Math.min(remainingIncomeProvincial - bracket.min, bracket.max - bracket.min);
        provincialTax += taxableInBracket * bracket.rate;
        remainingIncomeProvincial -= taxableInBracket;
      }
      
      if (remainingIncomeProvincial <= 0) break;
    }
    
    // Calculate age-based credits
    let ageCredits = 0;
    if (age >= 65) {
      // Age amount credit
      let baseAmount = 8000;
      const clawbackRate = 0.15;
      const clawbackThreshold = 40000;
      
      if (taxableIncome > clawbackThreshold) {
        const reduction = (taxableIncome - clawbackThreshold) * clawbackRate;
        baseAmount = Math.max(0, baseAmount - reduction);
      }
      
      ageCredits = baseAmount * 0.15; // 15% credit rate
    }
    
    // Total tax = Federal + Provincial - Credits
    const totalTax = federalTax + provincialTax - ageCredits;
    
    // Debug logging for tax calculation
    if (process.env.NODE_ENV === 'development') {
      console.log(`Tax Calculation Debug - Age: ${age}, Income: $${taxableIncome.toLocaleString()}`);
      console.log(`Federal Tax: $${federalTax.toLocaleString()}, Provincial Tax: $${provincialTax.toLocaleString()}`);
      console.log(`Age Credits: $${ageCredits.toLocaleString()}, Total Tax: $${totalTax.toLocaleString()}`);
      console.log(`Effective Rate: ${((totalTax / taxableIncome) * 100).toFixed(1)}%`);
    }
    
    return Math.max(0, totalTax);
  };

  // OAS calculation with comprehensive clawback and delay strategies
  const calculateOAS = (age: number, taxableIncome: number, yearsDelay: number = 0) => {
    // Use the improved OAS service for accurate calculations
    const oasResult = OASService.calculateOAS({
      age,
      taxableIncome,
      yearsDelay,
      residencyYears: userProfile.residencyYears || 40, // Default to full residency
      currentAge: userProfile.age
    });
    
    return oasResult.annualBenefit;
  };

  // CPP calculation with delay strategies
  const calculateCPP = (age: number, yearsDelay: number = 0) => {
    const currentAge = userProfile.age;
    const inflationYear = age - currentAge;
    
    // CPP delay strategy (max 5 years)
    yearsDelay = Math.min(5, Math.max(0, yearsDelay));
    const minCPPAge = 65 + yearsDelay;
    const topUp = 0.007 * 12 * yearsDelay; // 0.7% per month delay
    
    if (age <= minCPPAge) return 0;
    
    // Base CPP amount (2024: $1,364.90/month = $16,379/year)
    let baseCPP = 16379 * Math.pow(1.03, inflationYear); // Inflation adjusted
    baseCPP *= (1 + topUp); // Apply delay bonus
    
    return baseCPP;
  };

  const renderAccountBalanceBarChart = () => {
    if (!results) return null;

    const accountTypes = [
      { key: 'ALL', label: 'All Accounts', color: '#4CAF50' },
      { key: 'RRSP_RRIF', label: 'RRSP/RRIF', color: '#2196F3' },
      { key: 'TFSA', label: 'TFSA', color: '#FF9800' },
      { key: 'OTHER_INVESTMENTS', label: 'Other Investments', color: '#9C27B0' },
    ];
    
    // Calculate retirement and RRIF years
    const retirementYear = userProfile.currentYear + (userProfile.retirementAge - userProfile.age);
    const rrifYear = userProfile.currentYear + (71 - userProfile.age); // RRIF starts at 71
    const oasYear = userProfile.currentYear + (65 - userProfile.age); // OAS starts at 65
    const cppYear = userProfile.currentYear + ((userProfile.cppStartAge || 65) - userProfile.age);

    // Generate sample data for demonstration
    const years = Array.from({ length: userProfile.lifeExpectancy - userProfile.age + 1 }, (_, i) => userProfile.currentYear + i);
    
    // Generate account balance data
    const accountData = toChartSeries(results?.scenarios?.average?.detailed_projection_data);

    const maxBalance = Math.min(Math.max(...accountData.map(d => d.total)), 10000000); // Cap at 10M
    const chartWidth = 900;
    const chartHeight = 400;
    const margin = { top: 40, right: 20, bottom: 60, left: 80 };
    const barWidth = (chartWidth - margin.left - margin.right) / accountData.length * 0.8;

    return (
      <div className="account-balance-chart-container">
        <div className="chart-header">
          <h4>Account Balance Projections</h4>
          <div className="account-selector">
            <label>Select Account:</label>
            <select 
              value={selectedAccount} 
              onChange={(e) => setSelectedAccount(e.target.value)}
            >
              {accountTypes.map(account => (
                <option key={account.key} value={account.key}>
                  {account.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="bar-chart-container">
          <svg width={chartWidth} height={chartHeight} className="bar-chart-svg">
            {/* Chart title */}
            <text x={chartWidth / 2} y="20" textAnchor="middle" className="chart-title">
              Account Balance Over Time
            </text>
            
            {/* X-axis */}
            <line x1={margin.left} y1={chartHeight - margin.bottom} x2={chartWidth - margin.right} y2={chartHeight - margin.bottom} stroke="#333" strokeWidth="2" />
            
            {/* Y-axis */}
            <line x1={margin.left} y1={margin.top} x2={margin.left} y2={chartHeight - margin.bottom} stroke="#333" strokeWidth="2" />
            
            {/* Y-axis labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => {
              const y = margin.top + (chartHeight - margin.top - margin.bottom) * (1 - tick);
              const value = maxBalance * tick;
              return (
                <g key={i}>
                  <line x1={margin.left - 5} y1={y} x2={margin.left} y2={y} stroke="#333" strokeWidth="1" />
                  <text x={margin.left - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#666">
                    {formatCurrency(value)}
                  </text>
                </g>
              );
            })}
            
            {/* X-axis labels (every 5 years) */}
            {accountData.filter((_, i) => i % 5 === 0).map((data, i) => {
              const x = margin.left + (i * 5) * (chartWidth - margin.left - margin.right) / accountData.length;
              return (
                <text key={i} x={x} y={chartHeight - margin.bottom + 15} textAnchor="middle" fontSize="10" fill="#666">
                  {data.year}
                </text>
              );
            })}
            
            {/* Bars */}
            {accountData.map((data, index) => {
              const x = margin.left + index * (chartWidth - margin.left - margin.right) / accountData.length + (chartWidth - margin.left - margin.right) / accountData.length * 0.1;
              let balance = 0;
              let color = '#4CAF50';
              
              switch (selectedAccount) {
                case 'RRSP_RRIF':
                  balance = data.rrsp;
                  color = '#2196F3';
                  break;
                case 'TFSA':
                  balance = data.tfsa;
                  color = '#FF9800';
                  break;
                case 'OTHER_INVESTMENTS':
                  balance = data.other;
                  color = '#9C27B0';
                  break;
                default:
                  balance = data.total;
                  color = '#4CAF50';
              }
              
              const barHeight = (balance / maxBalance) * (chartHeight - margin.top - margin.bottom);
              const y = margin.top + (chartHeight - margin.top - margin.bottom) - barHeight;
              
              return (
                <g key={index}>
                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    fill={color}
                    stroke="#333"
                    strokeWidth="1"
                  />
                  {/* Bar value label */}
                  {index % 5 === 0 && (
                    <text
                      x={x + barWidth / 2}
                      y={y - 5}
                      textAnchor="middle"
                      fontSize="8"
                      fill="#333"
                    >
                      {formatCurrency(balance)}
                    </text>
                  )}
                </g>
              );
            })}
            
            {/* Retirement flag */}
            <line x1={margin.left + (retirementYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y1={margin.top} 
                  x2={margin.left + (retirementYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y2={chartHeight - margin.bottom} 
                  stroke="#9C27B0" strokeWidth="2" strokeDasharray="5,5" />
            <text x={margin.left + (retirementYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y={margin.top - 10} 
                  textAnchor="middle" className="flag-label">Retirement</text>
            
            {/* RRIF flag */}
            <line x1={margin.left + (rrifYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y1={margin.top} 
                  x2={margin.left + (rrifYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y2={chartHeight - margin.bottom} 
                  stroke="#FF9800" strokeWidth="2" strokeDasharray="5,5" />
            <text x={margin.left + (rrifYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y={margin.top - 25} 
                  textAnchor="middle" className="flag-label">RRIF</text>
            
            {/* OAS flag */}
            <line x1={margin.left + (oasYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y1={margin.top} 
                  x2={margin.left + (oasYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y2={chartHeight - margin.bottom} 
                  stroke="#4CAF50" strokeWidth="2" strokeDasharray="5,5" />
            <text x={margin.left + (oasYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y={margin.top - 40} 
                  textAnchor="middle" className="flag-label">OAS</text>
            
            {/* CPP flag */}
            <line x1={margin.left + (cppYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y1={margin.top} 
                  x2={margin.left + (cppYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y2={chartHeight - margin.bottom} 
                  stroke="#2196F3" strokeWidth="2" strokeDasharray="5,5" />
            <text x={margin.left + (cppYear - userProfile.currentYear) * (chartWidth - margin.left - margin.right) / accountData.length} y={margin.top - 55} 
                  textAnchor="middle" className="flag-label">CPP</text>
          </svg>
        </div>
        
        <div className="chart-notes">
          <h5>Chart Notes:</h5>
          <ul>
            <li>Shows projected account balances from current age ({userProfile.age}) to life expectancy ({userProfile.lifeExpectancy})</li>
            <li>Includes contributions, withdrawals, and investment returns based on your settings</li>
            <li>Accounts for required RRIF withdrawals starting at age 71</li>
            <li>Shows how balances peak during working years and decline during retirement</li>
            <li>Dashed lines represent key life events: Retirement, RRIF start, OAS start, CPP start</li>
          </ul>
        </div>
      </div>
    );
  };

  const handleSaveScenario = async (data: UserProfile = userProfile) => {
    const currentUser = TylerState.user;
    if (!currentUser) {
      // Trigger login modal
      window.dispatchEvent(new Event('open-login-modal'));
      setGuidanceTitle('Login Required');
      setGuidanceContent('Please log in to save scenarios. Free accounts can save 1 scenario, supporters get unlimited saves.');
      setShowGuidanceModal(true);
      return;
    }

    // Get tier limit
    const cap = getScenarioCap(currentUser.tier || 'free');

    // Check if we're overwriting an existing scenario
    const name = (saveName || '').trim();
    const finalName = name || `Retirement Scenario ${new Date().toLocaleString()}`;
    const existingScenario = savedScenarios.find(s => s.name.toLowerCase() === finalName.toLowerCase());

    // Only check tier limits if we're creating a new scenario (not overwriting)
    if (!existingScenario && savedScenarios.length >= cap) {
      setGuidanceTitle('Save Limit Reached');
      setGuidanceContent(`Free accounts can save ${cap} scenario${cap === 1 ? '' : 's'}.<br><br>Upgrade to Supporter ($10/year) for unlimited saves and advanced features.`);
      setShowGuidanceModal(true);
      setShowSaveModal(false);
      return;
    }

    console.log('Saving retirement scenario to backend:', data);

    try {
      if (existingScenario) {
        // Update existing scenario
        await retirementScenariosService.updateRetirementScenario(existingScenario.id, {
          name: finalName,
          description: saveDescription.trim() || undefined,
          data: data
        });
        console.log('Updated existing retirement scenario:', finalName);
      } else {
        // Create new scenario
        await retirementScenariosService.saveRetirementScenario({
          name: finalName,
          description: saveDescription.trim(),
          data: data
        });
        console.log('Created new retirement scenario:', finalName);
      }

      // Reload scenarios
      await loadSavedScenarios();

      // Store in TylerState
      TylerState.retirement.userProfile = data;
      updateReact();

      setSaveDescription('');
      setShowSaveModal(false);
      setGuidanceTitle(existingScenario ? 'Updated' : 'Saved');
      setGuidanceContent(`Your retirement scenario was ${existingScenario ? 'updated' : 'saved'}. You can load it anytime from the Scenarios tab.`);
      setShowGuidanceModal(true);
    } catch (error) {
      console.error('Error saving scenario:', error);
      setGuidanceTitle('Save Failed');
      setGuidanceContent('Failed to save scenario. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    try {
      await retirementScenariosService.deleteRetirementScenario(scenarioId);
      await loadSavedScenarios();
      setGuidanceTitle('Deleted');
      setGuidanceContent('Scenario deleted successfully.');
      setShowGuidanceModal(true);
    } catch (error) {
      console.error('Error deleting scenario:', error);
      setGuidanceTitle('Delete Failed');
      setGuidanceContent('Failed to delete scenario. Please try again.');
      setShowGuidanceModal(true);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      // Store current results and userProfile in TylerState for PDF service to read
      TylerState.retirement.results = taxOptimizationResults || results;
      // The PDF service reads backend-shaped keys (rrsp_balance, retirement_age, annual_spending...)
      TylerState.retirement.userProfile = AdvancedRetirementAPI.convertToBackendFormat(userProfile);
      TylerState.retirement.inflationView = inflationView;
      TylerState.retirement.benefitAgesLocked = {
        cpp: userProfile.cppDelayStrategy !== 'Calculate Suggested Strategy',
        oas: userProfile.oasDelayStrategy !== 'Calculate Suggested Strategy'
      };

      const pdfService = new RetirementPDFService();
      const scenarioName = saveName || 'Retirement Analysis';
      await pdfService.generateProfessionalPDF(scenarioName);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  const renderResults = () => {
    // Show tax optimization results if available (new comprehensive format)
    if (taxOptimizationResults) {
      return (
        <div className="results-section">
          <div className="results-header">
            <h2>🎯 Optimized Retirement Strategy</h2>
            <button onClick={() => handleSaveScenario()} className="save-btn">
              💾 Save Scenario
            </button>
          </div>
          <RetirementResults results={taxOptimizationResults} />
        </div>
      );
    }

    // Show old Monte Carlo results if available (legacy format)
    if (!results) return null;

    // Debug: Log the results structure
    console.log('Results structure:', JSON.stringify(results, null, 2));

    return (
      <div className="results-section">
        <div className="results-header">
          <h2>Retirement Analysis Results</h2>
          <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
            <div className="inflation-toggle">
              <label style={{marginRight: '8px', fontSize: '14px', color: '#666'}}>View:</label>
              <button
                onClick={() => setInflationView('real')}
                className={inflationView === 'real' ? 'toggle-btn active' : 'toggle-btn'}
                style={{
                  padding: '6px 12px',
                  border: inflationView === 'real' ? '2px solid #007bff' : '1px solid #ddd',
                  background: inflationView === 'real' ? '#e7f3ff' : 'white',
                  borderRadius: '4px 0 0 4px',
                  cursor: 'pointer',
                  fontWeight: inflationView === 'real' ? 'bold' : 'normal'
                }}
              >
                Real (Today's $)
              </button>
              <button
                onClick={() => setInflationView('nominal')}
                className={inflationView === 'nominal' ? 'toggle-btn active' : 'toggle-btn'}
                style={{
                  padding: '6px 12px',
                  border: inflationView === 'nominal' ? '2px solid #007bff' : '1px solid #ddd',
                  background: inflationView === 'nominal' ? '#e7f3ff' : 'white',
                  borderRadius: '0 4px 4px 0',
                  cursor: 'pointer',
                  fontWeight: inflationView === 'nominal' ? 'bold' : 'normal'
                }}
              >
                Nominal (Future $)
              </button>
            </div>
            <button onClick={() => handleSaveScenario()} className="save-btn">
              💾 Save Scenario
            </button>
            <button
              onClick={handleExportPDF}
              className="save-btn"
              disabled={exportingPDF}
              style={{opacity: exportingPDF ? 0.6 : 1, cursor: exportingPDF ? 'wait' : 'pointer'}}
            >
              {exportingPDF ? '⏳ Generating PDF...' : '📄 Export to PDF'}
            </button>
          </div>
        </div>

        {/* BIG RESULT - Main outcome */}
        <div className="big-result">
          <h3>
            {(() => {
              const balance = results.summary?.percentiles?.p50_balance || results.summary_stats?.median_final_balance;
              if (!balance) return 'Analysis Complete';

              // Get the ending year from detailed data
              const endYear = results.scenarios?.average?.detailed_projection_data?.slice(-1)[0]?.year || userProfile.currentYear;
              const inflatedBalance = applyInflation(balance, endYear);

              return `Projected Ending Balance: ${formatCurrency(inflatedBalance)}`;
            })()}
          </h3>
          <div style={{fontSize: '0.9em', color: '#7f8c8d'}}>
            Based on {results.summary?.total_scenarios || results.summary_stats?.total_scenarios || 'multiple'} scenarios
            {inflationView === 'nominal' && ' (in future dollars)'}
            {inflationView === 'real' && ' (in today\'s dollars)'}
          </div>
        </div>

        {/* KEY INSIGHTS */}
        <div className="key-insights">
          <h3>Key Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <h4>Success Rate</h4>
              <div className="insight-value">
                {results.summary_stats?.avg_success_rate ?
                  formatPercentage(results.summary_stats.avg_success_rate) :
                  'N/A'
                }
              </div>
              <div className="insight-description">
                Percentage of scenarios where you don't run out of money
              </div>
            </div>

            <div className="insight-card">
              <h4>Optimal RRSP Contribution</h4>
              <div className="insight-value">
                {results.summary_stats?.optimal_rrsp_contribution ?
                  formatCurrency(results.summary_stats.optimal_rrsp_contribution) :
                  'N/A'
                }
              </div>
              <div className="insight-description">
                Recommended annual RRSP contribution
              </div>
            </div>

            <div className="insight-card">
              <h4>Risk Range</h4>
              <div className="insight-value">
                {(() => {
                  const endYear = results.scenarios?.average?.detailed_projection_data?.slice(-1)[0]?.year || userProfile.currentYear;

                  if (results.summary?.percentiles?.p25_balance && results.summary?.percentiles?.p75_balance) {
                    const p25 = applyInflation(results.summary.percentiles.p25_balance, endYear);
                    const p75 = applyInflation(results.summary.percentiles.p75_balance, endYear);
                    return `${formatCurrency(p25)} - ${formatCurrency(p75)}`;
                  }

                  if (results.summary_stats?.min_final_balance && results.summary_stats?.max_final_balance) {
                    const min = applyInflation(results.summary_stats.min_final_balance, endYear);
                    const max = applyInflation(results.summary_stats.max_final_balance, endYear);
                    return `${formatCurrency(min)} - ${formatCurrency(max)}`;
                  }

                  return 'N/A';
                })()}
              </div>
              <div className="insight-description">
                25th to 75th percentile range
              </div>
            </div>
          </div>
        </div>

        {/* ACCOUNT PRESERVATION SCORE */}
        {results.scenarios && (
          <AccountPreservationScore
            scenarios={results.scenarios}
            lifeExpectancy={userProfile.lifeExpectancy}
            currentAge={userProfile.age}
          />
        )}

        {/* DETAILED BREAKDOWN */}
        <div className="detailed-breakdown">
          <div className="detail-view-selector">
            <h3>Detailed Analysis</h3>
            <div className="detail-cards">
              <button
                className={`detail-card ${showDetailedAnalysis ? 'active' : ''}`}
                onClick={() => setShowDetailedAnalysis(!showDetailedAnalysis)}
                aria-pressed={showDetailedAnalysis}
              >
                <div className="card-icon">📊</div>
                <div className="card-title">Account Balance Projections</div>
                <div className="card-desc">Interactive bar chart with account selection</div>
              </button>

              <button
                className={`detail-card ${showScenarioDistribution ? 'active' : ''}`}
                onClick={() => setShowScenarioDistribution(!showScenarioDistribution)}
                aria-pressed={showScenarioDistribution}
              >
                <div className="card-icon">📈</div>
                <div className="card-title">Scenario Distribution</div>
                <div className="card-desc">Risk analysis with failure rate</div>
              </button>

              <button
                className={`detail-card ${showAccountContinuity ? 'active' : ''}`}
                onClick={() => setShowAccountContinuity(!showAccountContinuity)}
                aria-pressed={showAccountContinuity}
              >
                <div className="card-icon">📋</div>
                <div className="card-title">Account Continuity</div>
                <div className="card-desc">Year-by-year account breakdown</div>
              </button>

              <button
                className={`detail-card ${showMonthlyBreakdown ? 'active' : ''}`}
                onClick={() => setShowMonthlyBreakdown(!showMonthlyBreakdown)}
                aria-pressed={showMonthlyBreakdown}
              >
                <div className="card-icon">📅</div>
                <div className="card-title">Monthly Cash Flow</div>
                <div className="card-desc">Month-by-month income and spending</div>
              </button>

              <button
                className={`detail-card ${showTaxBracketAlerts ? 'active' : ''}`}
                onClick={() => setShowTaxBracketAlerts(!showTaxBracketAlerts)}
                aria-pressed={showTaxBracketAlerts}
              >
                <div className="card-icon">💰</div>
                <div className="card-title">Tax Bracket Opportunities</div>
                <div className="card-desc">Optimize withdrawals by bracket</div>
              </button>

              <button
                className={`detail-card ${showStrategyExplainer ? 'active' : ''}`}
                onClick={() => setShowStrategyExplainer(!showStrategyExplainer)}
                aria-pressed={showStrategyExplainer}
              >
                <div className="card-icon">📖</div>
                <div className="card-title">Strategy Explained</div>
                <div className="card-desc">Understand why decisions are made</div>
              </button>
            </div>
          </div>

          {showDetailedAnalysis && (
            <div className="detailed-analysis-content">
              {/* Account Balance Chart */}
              <div className="chart-section">
                {renderAccountBalanceBarChart()}
              </div>
            </div>
          )}

          {showScenarioDistribution && (
            <div className="detailed-analysis-content">
              {/* Scenario Distribution Chart */}
              <div className="chart-section">
                {renderScenarioDistributionChart()}
              </div>
            </div>
          )}

          {showAccountContinuity && (
            <div className="detailed-analysis-content">
              {/* Account Continuity Tables */}
              <div className="chart-section">
                {renderAccountContinuityTables()}
              </div>
            </div>
          )}

          {showMonthlyBreakdown && (
            <div className="detailed-analysis-content">
              {/* Monthly Breakdown */}
              <div className="chart-section">
                {results?.scenarios?.average?.detailed_projection_data ? (
                  <MonthlyBreakdown
                    detailedData={results.scenarios.average.detailed_projection_data}
                    inflationView={inflationView}
                    applyInflation={applyInflation}
                  />
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Run a simulation to see this analysis.</p>
                )}
              </div>
            </div>
          )}

          {showTaxBracketAlerts && (
            <div className="detailed-analysis-content">
              {/* Tax Bracket Alerts */}
              <div className="chart-section">
                {results?.scenarios?.average?.detailed_projection_data ? (
                  <TaxBracketAlerts
                    detailedData={results.scenarios.average.detailed_projection_data}
                    province={userProfile.province || 'Ontario'}
                    currentYear={userProfile.currentYear || new Date().getFullYear()}
                    inflationRate={userProfile.inflationRate || 0.03}
                  />
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Run a simulation to see this analysis.</p>
                )}
              </div>
            </div>
          )}

          {showStrategyExplainer && (
            <div className="detailed-analysis-content">
              {/* Withdrawal Strategy Explainer */}
              <div className="chart-section">
                {results?.scenarios?.average?.detailed_projection_data ? (
                  <WithdrawalStrategyExplainer
                    detailedData={results.scenarios.average.detailed_projection_data}
                    userAge={userProfile.age}
                    retirementAge={userProfile.retirementAge}
                    cppStartAge={parseInt(userProfile.cppDelayStrategy.match(/\d+/)?.[0] || '65')}
                    oasStartAge={parseInt(userProfile.oasDelayStrategy.match(/\d+/)?.[0] || '65')}
                  />
                ) : (
                  <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>Run a simulation to see this analysis.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderScenarioDistributionChart = () => {
    if (!results) return null;

    // Get the scenario data
    const p25 = results.summary?.percentiles?.p25_balance || results.summary_stats?.min_final_balance || 0;
    const p50 = results.summary?.percentiles?.p50_balance || results.summary_stats?.median_final_balance || 0;
    const p75 = results.summary?.percentiles?.p75_balance || results.summary_stats?.max_final_balance || 0;
    
    // Calculate failure rate (scenarios ending below $0)
    const failureRate = results.summary_stats?.avg_success_rate ? (1 - results.summary_stats.avg_success_rate) : 0;
    
    // Create a bell curve distribution
    const generateBellCurveData = () => {
      const data: Array<{x: number, y: number}> = [];
      const min = Math.max(0, p25 - (p50 - p25) * 2);
      const max = Math.min(p75 + (p75 - p50) * 2, 10000000); // Cap at 10M
      const steps = 50;
      
      for (let i = 0; i <= steps; i++) {
        const x = min + (max - min) * (i / steps);
        // Normal distribution centered around median
        const mu = p50;
        const sigma = (p75 - p25) / 1.35; // Approximate standard deviation
        const y = Math.exp(-0.5 * Math.pow((x - mu) / sigma, 2)) / (sigma * Math.sqrt(2 * Math.PI));
        data.push({ x, y: y * 100 }); // Scale for visualization
      }
      
      return data;
    };

    const bellData = generateBellCurveData();
    const chartWidth = 600;
    const chartHeight = 300;
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };

    return (
      <div className="scenario-distribution-container">
        <div className="chart-header">
          <h4>Scenario Distribution</h4>
          <div className="risk-indicator">
            <span className="failure-rate">Failure Rate: {formatPercentage(failureRate)}</span>
          </div>
        </div>
        
        <p className="chart-description">
          Distribution of 1,000+ scenario outcomes showing the range of possible ending balances
        </p>
        
        <svg width={chartWidth} height={chartHeight} className="scenario-chart-svg">
          {/* Chart background */}
          <rect 
            width={chartWidth - margin.left - margin.right} 
            height={chartHeight - margin.top - margin.bottom} 
            x={margin.left} 
            y={margin.top}
            fill="#f8f9fa" 
            stroke="#dee2e6" 
            strokeWidth="1" 
          />
          
          {/* Bell curve */}
          <path
            d={bellData.map((point, i) => {
              const x = margin.left + (point.x - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right);
              const y = margin.top + (chartHeight - margin.top - margin.bottom) - (point.y / Math.max(...bellData.map(d => d.y))) * (chartHeight - margin.top - margin.bottom);
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ')}
            fill="none"
            stroke="#dc2626"
            strokeWidth="3"
          />
          
          {/* Fill area under curve */}
          <path
            d={bellData.map((point, i) => {
              const x = margin.left + (point.x - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right);
              const y = margin.top + (chartHeight - margin.top - margin.bottom) - (point.y / Math.max(...bellData.map(d => d.y))) * (chartHeight - margin.top - margin.bottom);
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            }).join(' ') + ` L ${margin.left + (chartWidth - margin.left - margin.right)} ${margin.top + (chartHeight - margin.top - margin.bottom)} L ${margin.left} ${margin.top + (chartHeight - margin.top - margin.bottom)} Z`}
            fill="rgba(220, 38, 38, 0.1)"
          />
          
          {/* X-axis */}
          <line 
            x1={margin.left} 
            y1={margin.top + (chartHeight - margin.top - margin.bottom)} 
            x2={chartWidth - margin.right} 
            y2={margin.top + (chartHeight - margin.top - margin.bottom)} 
            stroke="#666" 
            strokeWidth="2" 
          />
          
          {/* Y-axis */}
          <line 
            x1={margin.left} 
            y1={margin.top} 
            x2={margin.left} 
            y2={margin.top + (chartHeight - margin.top - margin.bottom)} 
            stroke="#666" 
            strokeWidth="2" 
          />
          
          {/* X-axis labels */}
          <text x={chartWidth / 2} y={chartHeight - 5} textAnchor="middle" fontSize="12" fill="#666">
            Ending Balance ($)
          </text>
          
          {/* X-axis tick marks and labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((tick, i) => {
            const x = margin.left + (chartWidth - margin.left - margin.right) * tick;
            const value = bellData[0].x + (bellData[bellData.length - 1].x - bellData[0].x) * tick;
            return (
              <g key={i}>
                <line 
                  x1={x} 
                  y1={margin.top + (chartHeight - margin.top - margin.bottom)} 
                  x2={x} 
                  y2={margin.top + (chartHeight - margin.top - margin.bottom) + 5} 
                  stroke="#666" 
                  strokeWidth="1" 
                />
                <text 
                  x={x} 
                  y={margin.top + (chartHeight - margin.top - margin.bottom) + 20} 
                  textAnchor="middle" 
                  fontSize="10" 
                  fill="#666"
                >
                  {formatCurrency(value)}
                </text>
              </g>
            );
          })}
          
          {/* Y-axis labels */}
          <text x={10} y={chartHeight / 2} textAnchor="middle" fontSize="12" fill="#666" transform={`rotate(-90, 10, ${chartHeight / 2})`}>
            Probability Density
          </text>
          
          {/* Percentile markers */}
          <line 
            x1={margin.left + (p25 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y1={margin.top} 
            x2={margin.left + (p25 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y2={margin.top + (chartHeight - margin.top - margin.bottom)} 
            stroke="#ff6b6b" 
            strokeWidth="2" 
            strokeDasharray="5,5" 
          />
          <text 
            x={margin.left + (p25 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y={margin.top - 5} 
            textAnchor="middle" 
            fontSize="10" 
            fill="#ff6b6b"
          >
            25th
          </text>
          
          <line 
            x1={margin.left + (p50 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y1={margin.top} 
            x2={margin.left + (p50 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y2={margin.top + (chartHeight - margin.top - margin.bottom)} 
            stroke="#4ecdc4" 
            strokeWidth="2" 
            strokeDasharray="5,5" 
          />
          <text 
            x={margin.left + (p50 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y={margin.top - 5} 
            textAnchor="middle" 
            fontSize="10" 
            fill="#4ecdc4"
          >
            50th
          </text>
          
          <line 
            x1={margin.left + (p75 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y1={margin.top} 
            x2={margin.left + (p75 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y2={margin.top + (chartHeight - margin.top - margin.bottom)} 
            stroke="#45b7d1" 
            strokeWidth="2" 
            strokeDasharray="5,5" 
          />
          <text 
            x={margin.left + (p75 - bellData[0].x) / (bellData[bellData.length - 1].x - bellData[0].x) * (chartWidth - margin.left - margin.right)} 
            y={margin.top - 5} 
            textAnchor="middle" 
            fontSize="10" 
            fill="#45b7d1"
          >
            75th
          </text>
        </svg>
        
        <div className="chart-notes">
          <h5>Chart Notes:</h5>
          <ul>
            <li>Shows the distribution of possible ending balances across all scenarios</li>
            <li>Failure rate indicates scenarios ending below $0</li>
            <li>25th, 50th, and 75th percentile markers show key risk points</li>
            <li>Wider distribution indicates higher uncertainty</li>
          </ul>
        </div>
      </div>
    );
  };

  const renderAccountContinuityTables = () => {
    if (!results) return null;

    const years = Array.from({ length: userProfile.lifeExpectancy - userProfile.age + 1 }, (_, i) => userProfile.age + i);
    
    const accountTypes = [
      { key: 'rrsp', label: 'RRSP/RRIF' },
      { key: 'lira', label: 'LIRA/LIF' },
      { key: 'tfsa', label: 'TFSA' },
      { key: 'other', label: 'Other Investments' },
      { key: 'corporate', label: 'Corporate' },
      { key: 'spouseRrsp', label: 'Spouse RRSP/RRIF' },
      { key: 'spouseLira', label: 'Spouse LIRA/LIF' },
      { key: 'spouseTfsa', label: 'Spouse TFSA' },
      { key: 'tax', label: 'Tax Summary' }
    ];

    const generateContinuityData = () => {
      const data: Array<any> = [];
      let rrspBalance = userProfile.rrspBalance || 0;
      let liraBalance = userProfile.liraBalance || 0;
      let tfsaBalance = userProfile.tfsaBalance || 0;
      let otherBalance = userProfile.nonRegisteredBalance || 0;
      let corporateBalance = userProfile.corporateBalance || 0;
      let spouseRrspBalance = userProfile.spouseRrspBalance || 0;
      let spouseLiraBalance = userProfile.spouseLiraBalance || 0;
      let spouseTfsaBalance = userProfile.spouseTfsaBalance || 0;
      let tfsaContributionRoom = 70000; // Starting TFSA room

      // Track previous year's ending balances to use as opening balances
      let prevRrspEndingBalance = rrspBalance;
      let prevTfsaEndingBalance = tfsaBalance;
      let prevOtherEndingBalance = otherBalance;
      let prevCorporateEndingBalance = corporateBalance;
      let prevSpouseRrspEndingBalance = spouseRrspBalance;
      let prevSpouseTfsaEndingBalance = spouseTfsaBalance;

      years.forEach((year, index) => {
        const age = userProfile.age + index;
        const isWorking = age < userProfile.retirementAge;
        const isDead = age > userProfile.lifeExpectancy;
        const isRRIFAge = age >= 71;
        
        // Calculate inflation factor for this year - moved to beginning to avoid declaration order issues
        const inflationFactor = Math.pow(1 + (userProfile.inflationRate || 0.025), index);
        
        // Spouse death logic
        let spouseIsDead = false;
        if (userProfile.spouseAge) {
          const spouseAge = (userProfile.spouseAge || 0) + index;
          spouseIsDead = spouseAge > userProfile.lifeExpectancy;
        }
        
        // RRSP/RRIF logic (combined account)
        let rrspAdditions = 0;
        let rrspWithdrawals = 0;
        
        if (isDead) {
          // User is dead - handle RRSP based on spouse status
          if (userProfile.spouseAge && !spouseIsDead) {
            // Spouse is still alive - transfer RRSP to spouse's RRSP (tax-free)
            spouseRrspBalance += rrspBalance;
            rrspWithdrawals = rrspBalance; // Show as withdrawal from user's RRSP
            rrspBalance = 0;
          } else {
            // Both spouses are dead - zero out RRSP and pay taxes
            rrspWithdrawals = rrspBalance;
            const taxesOnRRSP = rrspWithdrawals * 0.3; // Assume 30% tax rate on RRSP
            otherBalance -= taxesOnRRSP; // Pay taxes from other investments
            rrspBalance = 0;
          }
        } else if (isWorking) {
          // Only add to RRSP if we're not withdrawing in the same year
          if (rrspWithdrawals === 0) {
            const baseRrspAdditions = 18000;
            const rrspAdditions = baseRrspAdditions * inflationFactor;
            rrspBalance = rrspBalance * (1 + userProfile.rrspReturnRate / 100) + rrspAdditions;
          } else {
            // Cannot add and withdraw in same year - only grow existing balance
            rrspBalance = rrspBalance * (1 + userProfile.rrspReturnRate / 100);
          }
        } else {
          // Retirement withdrawals (RRIF minimums apply after 71)
          if (isRRIFAge) {
            rrspWithdrawals = calculateRRIFMinimumWithdrawal(age, rrspBalance); // RRIF minimum withdrawal
          } else {
            rrspWithdrawals = 40000; // Annual withdrawal
          }
          // Update balance: apply growth and subtract withdrawals
          rrspBalance = Math.max(0, rrspBalance * (1 + userProfile.rrspReturnRate / 100) - rrspWithdrawals);
        }
        
        // Calculate RRSP investment income
        const rrspInvestmentIncome = rrspBalance * (userProfile.rrspReturnRate / 100);
        
        // TFSA logic - used as a plug, but maximize contributions when possible
        let tfsaAdditions = 0;
        let tfsaWithdrawals = 0;
        
        if (isDead) {
          // Empty TFSA upon death - entire balance becomes withdrawal
          tfsaWithdrawals = tfsaBalance;
          otherBalance += tfsaBalance; // Move to investments
          tfsaBalance = 0;
        } else {
          tfsaBalance *= (1 + userProfile.tfsaReturnRate / 100); // User-defined growth
          
          // Maximize TFSA contributions when there's room
          const availableRoom = tfsaContributionRoom;
          const maxContribution = Math.min(7000, availableRoom); // Annual limit or available room
          
          if (isWorking && maxContribution > 0) {
            // Contribute to TFSA from other investments if possible
            const contribution = Math.min(maxContribution, otherBalance * 0.1); // Use 10% of other investments
            tfsaAdditions = contribution;
            tfsaBalance += contribution;
            otherBalance -= contribution;
            tfsaContributionRoom -= contribution;
          }
          
          // TFSA withdrawals if needed (plug logic)
          const totalOtherIncome = otherBalance * (userProfile.nonRegisteredReturnRate / 100); // Investment income
          const baseEmploymentIncome = isWorking ? 80000 : 40000;
          const employmentIncome = baseEmploymentIncome * inflationFactor;
          const totalIncome = totalOtherIncome + employmentIncome; // Employment income
          const baseAnnualExpenses = 60000;
          const totalExpenses = baseAnnualExpenses * inflationFactor; // Annual expenses
          
          if (totalIncome < totalExpenses) {
            const shortfall = totalExpenses - totalIncome;
            const withdrawal = Math.min(shortfall, tfsaBalance);
            tfsaWithdrawals = withdrawal;
            tfsaBalance -= withdrawal;
            otherBalance += withdrawal; // Move to investments
          }
        }
        
        // Calculate TFSA investment income using average balance (VBA logic)
        const tfsaInvestmentIncome = (tfsaBalance - tfsaAdditions + tfsaWithdrawals + (tfsaAdditions - tfsaWithdrawals) / 2) * (userProfile.tfsaReturnRate / 100);
        
        // Other investments - now properly includes TFSA inflows/outflows
        otherBalance *= (1 + userProfile.nonRegisteredReturnRate / 100); // User-defined growth
        
        // Employment income flows through investments - NOW INFLATES WITH USER'S RATE
        const baseEmploymentIncome = isWorking ? 80000 : 40000;
        const employmentIncome = baseEmploymentIncome * inflationFactor;
        otherBalance += employmentIncome;
        
        // Annual spending comes from investments - NOW INFLATES WITH USER'S RATE
        const baseAnnualSpending = 60000;
        const annualSpending = baseAnnualSpending * inflationFactor;
        otherBalance -= annualSpending;
        
        // RRSP/RRIF transfers - now properly handles same-year logic
        let rrspTransfer = 0;
        if (isWorking && rrspAdditions > 0) {
          rrspTransfer = -rrspAdditions; // Money flows OUT of investments TO RRSP
          otherBalance += rrspTransfer;
        } else if (rrspWithdrawals > 0) {
          rrspTransfer = rrspWithdrawals; // Money flows FROM RRSP TO investments
          otherBalance += rrspTransfer;
        }
        
        // TFSA transfers - now properly reflected in Other Investments
        let tfsaTransfer = 0;
        if (isDead) {
          tfsaTransfer = tfsaBalance; // Entire TFSA balance moves to investments upon death
          otherBalance += tfsaTransfer;
        } else if (isWorking && tfsaAdditions > 0) {
          tfsaTransfer = -tfsaAdditions; // Money flows OUT of investments TO TFSA
          otherBalance += tfsaTransfer;
        } else if (tfsaWithdrawals > 0) {
          tfsaTransfer = tfsaWithdrawals; // Money flows FROM TFSA TO investments
          otherBalance += tfsaTransfer;
        }
        
        // Corporate transfers
        let corporateTransfer = 0;
        if (isWorking) {
          const baseCorporateContribution = 15000;
          const corporateContribution = baseCorporateContribution * inflationFactor;
          corporateTransfer = -corporateContribution; // Money flows OUT of investments TO corporate
          otherBalance += corporateTransfer;
        } else {
          const baseCorporateWithdrawal = 20000;
          const corporateWithdrawal = baseCorporateWithdrawal * inflationFactor;
          corporateTransfer = corporateWithdrawal; // Money flows FROM corporate TO investments
          otherBalance += corporateTransfer;
        }
        
        // Government benefits flow into investments
        const oasIncome = calculateOAS(age, employmentIncome + rrspTransfer + corporateTransfer, userProfile.oasDelay || 0);
        const cppIncome = calculateCPP(age, userProfile.cppDelay || 0);
        otherBalance += oasIncome + cppIncome;
        
        // Calculate investment income using average balance (VBA logic) - exclude taxes to avoid circularity
        const otherInvestmentIncome = (otherBalance + (employmentIncome + annualSpending + rrspTransfer + tfsaTransfer + corporateTransfer + oasIncome + cppIncome) / 2) * (userProfile.nonRegisteredReturnRate / 100);
        
        // Taxes are deducted from investments
        const totalTaxableIncome = employmentIncome + otherInvestmentIncome + rrspTransfer + corporateTransfer + oasIncome + cppIncome;
        const taxesPaid = calculateTaxes(age, totalTaxableIncome);
        otherBalance -= taxesPaid;
        
        // Investment growth
        otherBalance *= (1 + userProfile.nonRegisteredReturnRate / 100); // User-defined growth

        // Corporate account variables (declare before cascading withdrawal)
        let corporateAdditions = 0;
        let corporateWithdrawals = 0;
        
        // Corporate account
        if (isWorking) {
          const baseCorporateAdditions = 15000;
          const corporateAdditions = baseCorporateAdditions * inflationFactor;
          corporateBalance = corporateBalance * (1 + userProfile.corporateReturnRate / 100) + corporateAdditions;
        } else {
          const baseCorporateWithdrawals = 20000;
          const corporateWithdrawals = baseCorporateWithdrawals * inflationFactor;
          corporateBalance = Math.max(0, corporateBalance * (1 + userProfile.corporateReturnRate / 100) - corporateWithdrawals);
        }
        
        // Calculate corporate investment income using average balance
        const corporateInvestmentIncome = (corporateBalance - corporateAdditions + corporateWithdrawals + (corporateAdditions - corporateWithdrawals) / 2) * (userProfile.corporateReturnRate / 100);
        
        // Spouse accounts
        let spouseRrspAdditions = 0;
        let spouseRrspWithdrawals = 0;
        let spouseTfsaAdditions = 0;
        let spouseTfsaWithdrawals = 0;
        
        if (userProfile.spouseAge) {
          const spouseAge = (userProfile.spouseAge || 0) + index;
          const spouseIsWorking = spouseAge < (userProfile.spouseRetirementAge || userProfile.retirementAge);
          const spouseIsDead = spouseAge > userProfile.lifeExpectancy;
          const spouseIsRRIFAge = spouseAge >= 71;
          
          if (spouseIsWorking) {
            // Only add to spouse RRSP if not withdrawing in same year
            if (spouseRrspWithdrawals === 0) {
              const baseSpouseRrspAdditions = 15000;
              const spouseRrspAdditions = baseSpouseRrspAdditions * inflationFactor;
              spouseRrspBalance = spouseRrspBalance * (1 + (userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate) / 100) + spouseRrspAdditions;
            } else {
              spouseRrspBalance = spouseRrspBalance * (1 + (userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate) / 100);
            }
            const baseSpouseTfsaAdditions = 7000;
            const spouseTfsaAdditions = baseSpouseTfsaAdditions * inflationFactor;
            spouseTfsaBalance = spouseTfsaBalance * (1 + (userProfile.spouseTfsaReturnRate || userProfile.tfsaReturnRate) / 100) + spouseTfsaAdditions;
          } else {
            if (spouseIsRRIFAge) {
              spouseRrspWithdrawals = calculateRRIFMinimumWithdrawal(spouseAge, spouseRrspBalance); // RRIF minimum
            } else {
              spouseRrspWithdrawals = 40000; // Annual withdrawal
            }
            spouseRrspBalance = Math.max(0, spouseRrspBalance * (1 + (userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate) / 100) - spouseRrspWithdrawals);
            
            // Handle spouse TFSA withdrawals
            if (spouseTfsaBalance > 0) {
              const spouseWithdrawal = Math.min(20000, spouseTfsaBalance); // Annual withdrawal
              spouseTfsaWithdrawals = spouseWithdrawal;
              spouseTfsaBalance -= spouseWithdrawal;
              // Add to other investments (spouse's money flows to joint investments)
              otherBalance += spouseWithdrawal;
            }
          }
          
          // Handle spouse death
          if (spouseIsDead) {
            // Transfer spouse RRSP to user's RRSP (tax-free)
            rrspBalance += spouseRrspBalance;
            spouseRrspBalance = 0;
            
            // Transfer spouse TFSA to other investments
            otherBalance += spouseTfsaBalance;
            spouseTfsaBalance = 0;
          }
        }

        // Update TFSA contribution room
        tfsaContributionRoom += 7000; // Annual TFSA limit

        // Calculate spouse investment income using average balance
        const spouseRrspInvestmentIncome = (spouseRrspBalance - spouseRrspAdditions + spouseRrspWithdrawals + (spouseRrspAdditions - spouseRrspWithdrawals) / 2) * ((userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate) / 100);
        const spouseTfsaInvestmentIncome = (spouseTfsaBalance - spouseTfsaAdditions + spouseTfsaWithdrawals + (spouseTfsaAdditions - spouseTfsaWithdrawals) / 2) * ((userProfile.spouseTfsaReturnRate || userProfile.tfsaReturnRate) / 100);

        // Cascading withdrawal logic - prevent Other Investments from going negative
        if (otherBalance < 0) {
          const shortfall = Math.abs(otherBalance);
          if (tfsaBalance > shortfall) {
            tfsaWithdrawals += shortfall;
            tfsaBalance -= shortfall;
            otherBalance += shortfall;
          } else if (rrspBalance > shortfall) {
            rrspWithdrawals += shortfall;
            rrspBalance -= shortfall;
            otherBalance += shortfall;
          } else {
            rrspWithdrawals += otherBalance;
            rrspBalance -= otherBalance;
            otherBalance = 0;
          }
        }

        // Store the current ending balances for next year's opening balances
        const currentRrspEndingBalance = Math.max(0, rrspBalance);
        const currentTfsaEndingBalance = Math.max(0, tfsaBalance);
        const currentOtherEndingBalance = Math.max(0, otherBalance);
        const currentCorporateEndingBalance = Math.max(0, corporateBalance);
        const currentSpouseRrspEndingBalance = Math.max(0, spouseRrspBalance);
        const currentSpouseTfsaEndingBalance = Math.max(0, spouseTfsaBalance);

        data.push({
          year,
          age,
          rrsp: {
            openingBalance: prevRrspEndingBalance,
            additions: rrspAdditions,
            withdrawals: rrspWithdrawals,
            investmentIncome: rrspInvestmentIncome,
            investmentIncomePercent: userProfile.rrspReturnRate,
            endingBalance: currentRrspEndingBalance
          },
          tfsa: {
            openingBalance: prevTfsaEndingBalance,
            additions: Math.max(0, tfsaAdditions), // Only show positive additions
            withdrawals: Math.max(0, tfsaWithdrawals) + Math.abs(Math.min(0, tfsaAdditions)), // Include negative additions as withdrawals
            investmentIncome: tfsaInvestmentIncome,
            investmentIncomePercent: userProfile.tfsaReturnRate,
            endingBalance: currentTfsaEndingBalance,
            contributionRoom: tfsaContributionRoom
          },
          other: {
            openingBalance: prevOtherEndingBalance,
            employmentIncome,
            annualSpending: -annualSpending, // Make negative for display
            rrspTransfer,
            tfsaTransfer,
            corporateTransfer,
            oasIncome,
            cppIncome,
            taxesPaid: -taxesPaid, // Make negative for display
            investmentIncome: otherInvestmentIncome, // Use the same calculation as tax table
            investmentIncomePercent: userProfile.nonRegisteredReturnRate,
            endingBalance: currentOtherEndingBalance
          },
          corporate: {
            openingBalance: prevCorporateEndingBalance,
            additions: corporateAdditions,
            withdrawals: corporateWithdrawals,
            investmentIncome: corporateInvestmentIncome,
            investmentIncomePercent: userProfile.corporateReturnRate,
            endingBalance: currentCorporateEndingBalance
          },
          spouseRrsp: {
            openingBalance: prevSpouseRrspEndingBalance,
            additions: spouseRrspAdditions,
            withdrawals: spouseRrspWithdrawals,
            investmentIncome: spouseRrspInvestmentIncome,
            investmentIncomePercent: userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate,
            endingBalance: currentSpouseRrspEndingBalance
          },
          spouseTfsa: {
            openingBalance: prevSpouseTfsaEndingBalance,
            additions: spouseTfsaAdditions,
            withdrawals: spouseTfsaWithdrawals,
            investmentIncome: spouseTfsaInvestmentIncome,
            investmentIncomePercent: userProfile.spouseTfsaReturnRate || userProfile.tfsaReturnRate,
            endingBalance: currentSpouseTfsaEndingBalance,
            contributionRoom: tfsaContributionRoom
          },
          tax: {
            employmentIncome,
            investmentIncome: otherInvestmentIncome,
            rrspWithdrawalContribution: isWorking ? -15000 : (isRRIFAge ? calculateRRIFMinimumWithdrawal(age, rrspBalance) : 40000), // Negative for contributions, positive for withdrawals
            corporateWithdrawal: isWorking ? -15000 : 20000, // Negative for contributions, positive for withdrawals
            oasIncome,
            cppIncome,
            totalTaxableIncome,
            taxesPaid,
            effectiveTaxRate: totalTaxableIncome > 0 ? (taxesPaid / totalTaxableIncome) * 100 : 0
          }
        });

        // Update previous ending balances for next iteration
        prevRrspEndingBalance = currentRrspEndingBalance;
        prevTfsaEndingBalance = currentTfsaEndingBalance;
        prevOtherEndingBalance = currentOtherEndingBalance;
        prevCorporateEndingBalance = currentCorporateEndingBalance;
        prevSpouseRrspEndingBalance = currentSpouseRrspEndingBalance;
        prevSpouseTfsaEndingBalance = currentSpouseTfsaEndingBalance;
      });

      return data;
    };

    const continuityData = generateContinuityData();

    const renderContinuityTable = (accountKey: string) => {
      const accountType = accountTypes.find(acc => acc.key === accountKey);
      if (!accountType) return null;

      const getTableHeaders = () => {
        switch (accountKey) {
          case 'tfsa':
          case 'spouseTfsa':
            return ['Year', 'Age', 'Opening Balance', 'Additions', 'Withdrawals', 'Investment Income $', 'Investment Income %', 'Ending Balance', 'Contribution Room'];
          case 'other':
            return ['Year', 'Age', 'Opening Balance', 'Employment Income', 'Annual Spending', 'RRSP/RRIF Transfer', 'TFSA Transfer', 'Corporate Transfer', 'OAS/CPP Income', 'Taxes Paid', 'Investment Income $', 'Investment Income %', 'Ending Balance'];
          case 'tax':
            return ['Year', 'Age', 'Employment Income', 'Investment Income', 'RRSP Withdrawal/Contribution', 'Corporate Withdrawal', 'OAS Income', 'CPP Income', 'Total Taxable Income', 'Taxes Paid', 'Effective Tax Rate %'];
          default:
            return ['Year', 'Age', 'Opening Balance', 'Additions', 'Withdrawals', 'Investment Income $', 'Investment Income %', 'Ending Balance'];
        }
      };

      const getRowData = (yearData: any, year: number, age: number) => {
        if (accountKey === 'tax') {
          return [
            year.toString(),
            age.toString(),
            formatCurrencyWithColor(yearData.employmentIncome),
            formatCurrencyWithColor(yearData.investmentIncome),
            formatCurrencyWithColor(yearData.rrspWithdrawalContribution),
            formatCurrencyWithColor(yearData.corporateWithdrawal),
            formatCurrencyWithColor(yearData.oasIncome),
            formatCurrencyWithColor(yearData.cppIncome),
            formatCurrencyWithColor(yearData.totalTaxableIncome),
            formatCurrencyWithColor(yearData.taxesPaid),
            `${yearData.effectiveTaxRate.toFixed(1)}%`
          ];
        }

        if (accountKey === 'other') {
          return [
            year.toString(),
            age.toString(),
            formatCurrencyWithColor(yearData.openingBalance),
            formatCurrencyWithColor(yearData.employmentIncome),
            formatCurrencyWithColor(yearData.annualSpending),
            formatCurrencyWithColor(yearData.rrspTransfer),
            formatCurrencyWithColor(yearData.tfsaTransfer),
            formatCurrencyWithColor(yearData.corporateTransfer),
            formatCurrencyWithColor(yearData.oasIncome + yearData.cppIncome),
            formatCurrencyWithColor(yearData.taxesPaid),
            formatCurrencyWithColor(yearData.investmentIncome),
            `${yearData.investmentIncomePercent.toFixed(1)}%`,
            formatCurrencyWithColor(yearData.endingBalance)
          ];
        }

        const baseData = [
          year.toString(),
          age.toString(),
          formatCurrencyWithColor(yearData.openingBalance),
          formatCurrencyWithColor(yearData.additions),
          formatCurrencyWithColor(yearData.withdrawals),
          formatCurrencyWithColor(yearData.investmentIncome),
          `${yearData.investmentIncomePercent.toFixed(1)}%`,
          formatCurrencyWithColor(yearData.endingBalance)
        ];

        if (accountKey === 'tfsa' || accountKey === 'spouseTfsa') {
          baseData.push(formatCurrencyWithColor(yearData.contributionRoom));
        }

        return baseData;
      };

      return (
        <div className="continuity-table-container">
          <h4>{accountType.label} Continuity</h4>
          <div className="table-wrapper">
            <table className="continuity-table">
              <thead>
                <tr>
                  {getTableHeaders().map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {continuityData.map((yearData, index) => {
                  const year = years[index];
                  const age = userProfile.age + index;
                  const accountYearData = yearData[accountKey.toLowerCase() as keyof typeof yearData];
                  return (
                    <tr key={index}>
                      {getRowData(accountYearData, year, age).map((cell, cellIndex) => (
                        <td key={cellIndex}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="account-continuity-container">
        <div className="continuity-header">
          <h4>Account Continuity Tables</h4>
          <div className="account-selector">
            <label>Select Account:</label>
            <select 
              value={selectedContinuityAccount} 
              onChange={(e) => setSelectedContinuityAccount(e.target.value)}
            >
              {accountTypes.map(account => (
                <option key={account.key} value={account.key}>
                  {account.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        {renderContinuityTable(selectedContinuityAccount)}
        
        <div className="chart-notes">
          <h5>Table Notes:</h5>
          <ul>
            <li>Shows year-by-year breakdown for {accountTypes.find(acc => acc.key === selectedContinuityAccount)?.label}</li>
            <li>Investment Income % represents the annual return rate</li>
            <li>TFSA Contribution Room shows available contribution space</li>
            <li>Negative withdrawals indicate money being taken out</li>
            <li>All amounts are in current year dollars</li>
          </ul>
        </div>
      </div>
    );
  };

  const handleLoadScenario = (data: UserProfile, scenarioName?: string) => {
    // Merge with current state to preserve any new fields with defaults
    setUserProfile(prev => ({ ...prev, ...data }));
    setGuidanceTitle('Scenario Loaded');
    setGuidanceContent(`"${scenarioName || 'Scenario'}" has been loaded successfully. You can now analyze or compare with other scenarios.`);
    setShowGuidanceModal(true);
  };

  // Wrapper function for ScenariosTab component
  const handleLoadScenarioWrapper = (data: BaseScenarioData, scenarioName?: string) => {
    handleLoadScenario(data as UserProfile, scenarioName);
  };

  const sections = [
    { id: 'general', label: 'General Settings', render: () => <GeneralSettingsSection userProfile={userProfile} handleInputChange={handleInputChange} savedScenarios={rrspScenarios} onLoadScenario={onLoadScenario} onDeleteScenario={onDeleteScenario} /> },
    { id: 'my-info', label: 'My Information', render: () => <MyInformationSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    ...(userProfile.maritalStatus === 'Married/Common-law' ? [
      { id: 'spouse-info', label: 'Spouse Information', render: () => <SpouseInformationSection userProfile={userProfile} handleInputChange={handleInputChange} /> }
    ] : []),
    { id: 'scenarios', label: 'Scenarios', render: () => (
      <ScenariosTab
        calculatorType="retirement"
        user={TylerState.user}
        onLoadScenarioToA={handleLoadScenarioWrapper}
        currentData={userProfile}
        onSaveScenario={handleSaveScenario}
      />
    )},
    { id: 'financial', label: 'Financial Balances', render: () => <FinancialBalancesSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    { id: 'investment', label: 'Investment Settings', render: () => <InvestmentSettingsSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    { id: 'income', label: 'Income & Employment', render: () => <IncomeEmploymentSection userProfile={userProfile} handleInputChange={handleInputChange} setGuidanceTitle={setGuidanceTitle} setGuidanceContent={setGuidanceContent} setShowGuidanceModal={setShowGuidanceModal} /> },
    { id: 'benefits', label: 'Government Benefits', render: () => <GovernmentBenefitsSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    { id: 'pension', label: 'Pension Information', render: () => <PensionSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    { id: 'additional-income', label: 'Additional Income', render: () => <OtherIncomeSection userProfile={userProfile} handleInputChange={handleInputChange} /> },
    { id: 'spending', label: 'Spending & Lifestyle', render: () => <SpendingLifestyleSection userProfile={userProfile} handleInputChange={handleInputChange} setGuidanceTitle={setGuidanceTitle} setGuidanceContent={setGuidanceContent} setShowGuidanceModal={setShowGuidanceModal} /> },
    { id: 'advanced', label: 'Advanced Settings', render: () => <AdvancedSettingsSection userProfile={userProfile} handleInputChange={handleInputChange} /> }
  ];

  // Add results sections when results are available
  const resultsSection = taxOptimizationResults ? [
    {
      id: 'results-summary',
      label: 'Results Summary',
      render: () => (
        <div className="results-section">
          <div className="results-header">
            <h2>🎯 Optimized Retirement Strategy</h2>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => handleSaveScenario()} className="save-btn">
                💾 Save Scenario
              </button>
              <button onClick={handleExportPDF} className="save-btn" disabled={exportingPDF}>
                {exportingPDF ? '⏳ Generating PDF...' : '📄 Export to PDF'}
              </button>
            </div>
          </div>

          {/* Comparison Card: Base vs Optimized */}
          {comparisonResults && comparisonResults.comparison && (
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '24px',
              borderRadius: '12px',
              marginBottom: '24px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                💰 Your Optimization Savings
              </h3>
              <div style={{ fontSize: '1.25rem', marginBottom: '20px', fontWeight: '500' }}>
                {comparisonResults.summary?.message ||
                  `By optimizing your retirement strategy, you can save ${formatCurrency(comparisonResults.comparison.total_benefit)}`}
              </div>

              <div className="comparison-grid" style={{ marginBottom: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.15)', padding: '16px', borderRadius: '8px' }}>
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '8px' }}>📊 Base Case (No Optimization)</h4>
                  <p style={{ fontSize: '0.9rem', marginBottom: '8px', opacity: 0.9 }}>
                    CPP/OAS at 65, minimal planning
                  </p>
                  <p style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Estate: {formatCurrency(comparisonResults.base_case?.median_estate || 0)}
                  </p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.25)', padding: '16px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.5)' }}>
                  <h4 style={{ fontSize: '1.1rem', marginBottom: '8px' }}>✨ Optimized Strategy</h4>
                  <p style={{ fontSize: '0.9rem', marginBottom: '8px', opacity: 0.9 }}>
                    Strategic tax planning
                  </p>
                  <p style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                    Estate: {formatCurrency(comparisonResults.optimized_case?.median_estate || 0)}
                  </p>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '0.95rem' }}>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ opacity: 0.9, fontSize: '0.85rem', marginBottom: '4px' }}>Estate Improvement</div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    +{formatCurrency(comparisonResults.comparison.estate_value?.difference || 0)}
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ opacity: 0.9, fontSize: '0.85rem', marginBottom: '4px' }}>Tax Savings</div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatCurrency(comparisonResults.comparison.lifetime_taxes?.savings || 0)}
                  </div>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', padding: '12px', borderRadius: '6px' }}>
                  <div style={{ opacity: 0.9, fontSize: '0.85rem', marginBottom: '4px' }}>OAS Clawback Avoided</div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {formatCurrency(comparisonResults.comparison.oas_clawback?.avoided || 0)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <RetirementResultsSummary results={taxOptimizationResults} formatCurrency={formatCurrency} />
        </div>
      )
    },
    {
      id: 'results-monthly',
      label: 'Monthly Breakdown',
      render: () => (
        <div className="results-section">
          <MonthlyBreakdown
            detailedData={taxOptimizationResults.representative_scenario?.year_by_year || []}
            inflationView={inflationView}
            applyInflation={applyInflation}
          />
        </div>
      )
    },
    {
      id: 'results-strategy',
      label: 'Strategy Explainer',
      render: () => (
        <div className="results-section">
          <WithdrawalStrategyExplainer
            detailedData={taxOptimizationResults.representative_scenario?.year_by_year || []}
            userAge={userProfile.age}
            retirementAge={userProfile.retirementAge || 65}
            cppStartAge={taxOptimizationResults.suggested_cpp_age || 65}
            oasStartAge={taxOptimizationResults.suggested_oas_age || 65}
          />
        </div>
      )
    },
    {
      id: 'results-tax-alerts',
      label: 'Tax Opportunities',
      render: () => (
        <div className="results-section">
          <TaxBracketAlerts
            detailedData={taxOptimizationResults.representative_scenario?.year_by_year || []}
            province={userProfile.province || 'AB'}
            currentYear={2024}
            inflationRate={userProfile.inflationRate || 2.5}
          />
        </div>
      )
    },
    {
      id: 'results-preservation',
      label: 'Preservation Score',
      render: () => (
        <div className="results-section">
          <AccountPreservationScore
            scenarios={taxOptimizationResults}
            lifeExpectancy={userProfile.lifeExpectancy || 90}
            currentAge={userProfile.age}
          />
        </div>
      )
    },
    {
      id: 'results-charts',
      label: 'Charts & Graphs',
      render: () => (
        <div className="results-section">
          <RetirementResultsCharts results={taxOptimizationResults} formatCurrency={formatCurrency} />
        </div>
      )
    },
    {
      id: 'results-table',
      label: 'Detailed Breakdown',
      render: () => (
        <div className="results-section">
          <RetirementResultsTable results={taxOptimizationResults} formatCurrency={formatCurrency} />
        </div>
      )
    }
  ] : [];

  // Combine input sections with results sections
  const allSections = [...sections, ...resultsSection];

  // Dynamic Next Button Component
  const NextButton = () => {
    const currentIndex = allSections.findIndex(s => s.id === activeSection);
    const nextSection = currentIndex < allSections.length - 1 ? allSections[currentIndex + 1] : null;

    // If we're at the last input section and haven't calculated yet, show Calculate button
    if (!nextSection && !taxOptimizationResults) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={() => {
              // Supporters: never. Logged-in: 20% chance. Guests: always.
              const currentUser = TylerState.user;
              const isSupporter = currentUser && currentUser.tier === 'supporter';
              const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
              if (showAd) {
                setShowAdModal(true);
              } else {
                setShowDisclaimerModal(true);
              }
            }}
            disabled={isOptimizing}
            className="run-simulation-btn primary"
          >
            {isOptimizing ? 'Analyzing...' : '🚀 Calculate Retirement Plan'}
          </button>
        </div>
      );
    }

    // If there's a next section (either input or output), show Next button
    if (nextSection) {
      return (
        <div className="dynamic-next-button">
          <button
            onClick={() => handleSectionClick(nextSection.id)}
            className="run-simulation-btn primary"
          >
            Next: {nextSection.label} →
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      {/* What came over from the Forecast, and what it had to work out to get here. Shown before
          anything is calculated, so a wrong inference is caught rather than inherited. */}
      {handoffNotes.length > 0 && (
        <div style={{ padding: '0.9rem 1.1rem', background: '#ecfdf5', borderLeft: '4px solid #059669',
          borderRadius: '8px', marginBottom: '1rem' }}>
          <strong style={{ color: '#065f46' }}>Filled in from your Personal Forecast</strong>
          {handoffNotes.map((note, i) => (
            <div key={i} style={{ fontSize: '0.85rem', color: '#475569', marginTop: '4px', lineHeight: 1.45 }}>{note}</div>
          ))}
          <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '6px' }}>
            Check these before you calculate - the Forecast does not know your province, your spouse,
            or your contribution room.
          </div>
          <button className="btn-secondary" onClick={() => setHandoffNotes([])}
            style={{ padding: '3px 10px', marginTop: '8px', fontSize: '0.8rem' }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Disclaimer Modal */}
      <DisclaimerModal
        isOpen={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        onAccept={handleDisclaimerAccept}
      />
      
      {/* Guidance Modal */}
      <GuidanceModal
        isOpen={showGuidanceModal}
        onClose={() => setShowGuidanceModal(false)}
        onAccept={() => setShowGuidanceModal(false)}
        title={guidanceTitle}
        content={guidanceContent}
      />

      {/* Ad Modal */}
      <GoogleAdInterstitial
        isOpen={showAdModal}
        onClose={() => {
          setShowAdModal(false);
          setShowDisclaimerModal(true);  // Show disclaimer after ad
        }}
      />

      {/* Main content */}
      <div className="content">
        {/* Navigation */}
        <nav className="mobile-nav">
          {allSections.map(section => (
            <button
              key={section.id}
              className={`nav-button ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => handleSectionClick(section.id)}
            >
              {section.label}
            </button>
          ))}
          <div style={{width: '100%'}}> &nbsp; </div>
                  {/* Run Analysis Button */}
                  <div className="mobile-actions">
                    <button
                      className="run-simulation-btn primary"
                      onClick={() => {
                        // Supporters: never. Logged-in: 20% chance. Guests: always.
                        const currentUser = TylerState.user;
                        const isSupporter = currentUser && currentUser.tier === 'supporter';
                        const showAd = !isSupporter && (!currentUser || Math.random() < 0.2);
                        if (showAd) {
                          setShowAdModal(true);
                        } else {
                          setShowDisclaimerModal(true);
                        }
                      }}
                      disabled={isOptimizing}
                      style={{
                        background: isOptimizing ? '#ccc' : '#28a745',
                        cursor: isOptimizing ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isOptimizing ? 'Analyzing...' : '🚀 Run Retirement Analysis'}
                    </button>
                  </div>
        </nav>

        {errors && errors.length > 0 && (
          <div className="error-messages" role="alert">
            <ul>
              {errors.map((message, index) => (
                <li key={index} style={{ whiteSpace: 'pre-line' }}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Sections */}
        {allSections.find(section => section.id === activeSection)?.render()}

        {/* Next Section Button */}
        <NextButton />
      </div>
    </div>
  );
};

export default AdvancedRetirementPlanner;

